import React from "react";
import { IScore } from "@eagerdog/interfaces";
import { duplicatePlacementSlots, PLACE_LABELS } from "@eagerdog/scoring-core";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";

import { dogLabel } from "./dogLabel";

import styles from "./UKCConformationResults.module.scss";

export const SELECT_OPTION: IOption = { id: "", value: "-- Select --" };
export const NOT_PLACED_OPTION: IOption = { id: "", value: "-- Not placed --" };

export function scoreOptions(
  scores: IScore[],
  empty: IOption = SELECT_OPTION,
  mode?: { junior?: boolean; breed?: boolean }
): IOption[] {
  return [
    empty,
    ...scores.map((score) => ({
      id: score._id,
      value: dogLabel(score, mode),
    })),
  ];
}

export function findOption(options: IOption[], id: string): IOption {
  return options.find((option) => option.id === id) || options[0];
}

export const SaveNote: React.FC<{ status: "idle" | "saving" | "saved" | "error" }> = ({ status }) => {
  const statusClass = status === "saved" ? styles.saved : status === "error" ? styles.error : "";
  return (
    <div className={`${styles.autoSaveStatus} ${statusClass}`}>
      {status === "saving" && "Saving…"}
      {status === "saved" && "✓ Saved"}
      {status === "error" && "Error saving"}
    </div>
  );
};

interface IAwardProps {
  label: string;
  scores: IScore[];
  value: string;
  onChange: (id: string) => void;
  breed?: boolean;
  junior?: boolean;
}

export const AwardDropdown: React.FC<IAwardProps> = ({ label, scores, value, onChange, breed, junior }) => {
  const options = scoreOptions(scores, SELECT_OPTION, { breed, junior });
  return (
    <Dropdown
      searchable
      label={label}
      placeholder={SELECT_OPTION.value}
      options={options}
      value={findOption(options, value)}
      onChange={(_event, option) => onChange(option.id)}
    />
  );
};

interface IGridProps {
  scores: IScore[];
  value: string[];
  onChange: (index: number, id: string) => void;
  junior?: boolean;
}

export const PlacementGrid: React.FC<IGridProps> = ({ scores, value, onChange, junior }) => {
  const options = scoreOptions(scores, NOT_PLACED_OPTION, { junior });
  const duplicateSlots = duplicatePlacementSlots(value);
  return (
    <>
      <div className={styles.classPlacementsGrid}>
        {PLACE_LABELS.map((place, index) => (
          <Dropdown
            key={place}
            searchable
            label={place}
            placeholder={NOT_PLACED_OPTION.value}
            options={options}
            value={findOption(options, value[index] || "")}
            onChange={(_event, option) => onChange(index, option.id)}
          />
        ))}
      </div>
      {duplicateSlots.length > 0 && (
        <div className={styles.placementWarning}>
          The same dog is placed more than once ({duplicateSlots.map((i) => PLACE_LABELS[i]).join(", ")}).
          Saved as entered — check the judge book.
        </div>
      )}
    </>
  );
};
